import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for auth endpoints (login/register failures should show error, not redirect)
      const requestUrl = error.config?.url || '';
      const isAuthEndpoint = requestUrl.includes('/auth/login') || 
                             requestUrl.includes('/auth/register') ||
                             requestUrl.includes('/auth/request-signup') ||
                             requestUrl.includes('/auth/verify-signup');
      
      // If on login/register page, don't redirect (let the page handle the error)
      const currentPathname = window.location.pathname;
      const isOnAuthPage = currentPathname === '/login' || currentPathname === '/register';
      
      if (isAuthEndpoint || isOnAuthPage) {
        // Let the component handle the error, don't redirect
        return Promise.reject(error);
      }
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Preserve the current path as redirect param (especially for /accept-invite)
      const currentPath = window.location.pathname + window.location.search;
      const isAcceptInvite = currentPath.includes('/accept-invite');
      const isResetPassword = currentPath.includes('/reset-password');
      
      if (isAcceptInvite || isResetPassword) {
        // Keep the user on the special page - they need to sign in with the redirect
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  googleCallback: (sessionId) => api.post('/auth/google/callback', { session_id: sessionId }),
  requestSignup: (data) => api.post('/auth/request-signup', data),
  verifySignup: (data) => api.post('/auth/verify-signup', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Admin APIs
export const adminAPI = {
  getPendingSignups: () => api.get('/admin/pending-signups'),
  deletePendingSignup: (id) => api.delete(`/admin/pending-signups/${id}`),
  getUsers: () => api.get('/admin/users'),
  toggleUserAdmin: (userId) => api.put(`/admin/users/${userId}/toggle-admin`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
};

// Team/Invite APIs
export const inviteAPI = {
  inviteToProject: (projectId, data) => api.post(`/projects/${projectId}/invites`, data),
  getProjectInvites: (projectId) => api.get(`/projects/${projectId}/invites`),
  cancelInvite: (projectId, inviteId) => api.delete(`/projects/${projectId}/invites/${inviteId}`),
  acceptInvite: (token) => api.post(`/invites/accept?token=${token}`),
  getMyInvites: () => api.get('/my-invites'),
  getProjectMembers: (projectId) => api.get(`/projects/${projectId}/members`),
  removeMember: (projectId, memberId) => api.delete(`/projects/${projectId}/members/${memberId}`),
  resendInvite: (projectId, inviteId) => api.post(`/projects/${projectId}/invites/${inviteId}/resend`),
  getEmailContent: (projectId, inviteId) => api.get(`/projects/${projectId}/invites/${inviteId}/email-content`),
  getInviteStats: (projectId) => api.get(`/projects/${projectId}/invite-stats`),
  trackInviteView: (token) => api.get(`/invites/track/${token}`),
};

// Activity Log APIs
export const activityAPI = {
  getProjectActivity: (projectId, limit = 50) => api.get(`/projects/${projectId}/activity?limit=${limit}`),
  getAllActivity: (limit = 100) => api.get(`/admin/activity?limit=${limit}`),
};

// Project APIs
export const projectAPI = {
  create: (data) => api.post('/projects', data),
  getAll: () => api.get('/projects'),
  getOne: (id) => api.get(`/projects/${id}`),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  export: (id) => api.get(`/projects/${id}/export`),
  transferOwnership: (projectId, newOwnerEmail) => api.post(`/projects/${projectId}/transfer`, { new_owner_email: newOwnerEmail }),
  importWBS: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${id}/import-wbs`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Task APIs
export const taskAPI = {
  create: (projectId, data) => api.post(`/projects/${projectId}/tasks`, data),
  getAll: (projectId) => api.get(`/projects/${projectId}/tasks`),
  update: (projectId, taskId, data) => api.put(`/projects/${projectId}/tasks/${taskId}`, data),
  delete: (projectId, taskId) => api.delete(`/projects/${projectId}/tasks/${taskId}`),
  bulkUpdate: (projectId, data) => api.put(`/projects/${projectId}/tasks/bulk`, data),
  bulkDelete: (projectId, taskIds) => api.post(`/projects/${projectId}/tasks/bulk-delete`, taskIds),
};

// Milestone APIs
export const milestoneAPI = {
  create: (projectId, data) => api.post(`/projects/${projectId}/milestones`, data),
  getAll: (projectId) => api.get(`/projects/${projectId}/milestones`),
  update: (projectId, milestoneId, data) => api.put(`/projects/${projectId}/milestones/${milestoneId}`, data),
  delete: (projectId, milestoneId) => api.delete(`/projects/${projectId}/milestones/${milestoneId}`),
};

// Column APIs
export const columnAPI = {
  create: (projectId, data) => api.post(`/projects/${projectId}/columns`, data),
  getAll: (projectId) => api.get(`/projects/${projectId}/columns`),
  update: (projectId, columnId, data) => api.put(`/projects/${projectId}/columns/${columnId}`, data),
  delete: (projectId, columnId) => api.delete(`/projects/${projectId}/columns/${columnId}`),
};

// Resource APIs
export const resourceAPI = {
  create: (projectId, data) => api.post(`/projects/${projectId}/resources`, data),
  getAll: (projectId) => api.get(`/projects/${projectId}/resources`),
  update: (projectId, resourceId, data) => api.put(`/projects/${projectId}/resources/${resourceId}`, data),
  delete: (projectId, resourceId) => api.delete(`/projects/${projectId}/resources/${resourceId}`),
};

// Risk APIs
export const riskAPI = {
  create: (projectId, data) => api.post(`/projects/${projectId}/risks`, data),
  getAll: (projectId) => api.get(`/projects/${projectId}/risks`),
  update: (projectId, riskId, data) => api.put(`/projects/${projectId}/risks/${riskId}`, data),
  delete: (projectId, riskId) => api.delete(`/projects/${projectId}/risks/${riskId}`),
};

// Document APIs
export const documentAPI = {
  create: (projectId, data) => api.post(`/projects/${projectId}/documents`, data),
  getAll: (projectId) => api.get(`/projects/${projectId}/documents`),
  update: (projectId, docId, data) => api.put(`/projects/${projectId}/documents/${docId}`, data),
  delete: (projectId, docId) => api.delete(`/projects/${projectId}/documents/${docId}`),
};

// Change Request APIs
export const changeRequestAPI = {
  create: (projectId, data) => api.post(`/projects/${projectId}/change-requests`, data),
  getAll: (projectId) => api.get(`/projects/${projectId}/change-requests`),
  update: (projectId, crId, data) => api.put(`/projects/${projectId}/change-requests/${crId}`, data),
  delete: (projectId, crId) => api.delete(`/projects/${projectId}/change-requests/${crId}`),
};

// Quality Check APIs
export const qualityCheckAPI = {
  create: (projectId, data) => api.post(`/projects/${projectId}/quality-checks`, data),
  getAll: (projectId) => api.get(`/projects/${projectId}/quality-checks`),
  update: (projectId, qcId, data) => api.put(`/projects/${projectId}/quality-checks/${qcId}`, data),
  delete: (projectId, qcId) => api.delete(`/projects/${projectId}/quality-checks/${qcId}`),
};

export default api;
